import React, { useState, useRef, useEffect } from 'react';
import FileUpload, { UploadedFile } from './FileUpload';
import SearchSourcesPicker from './SearchSourcesPicker';
import SharePointSitesPicker from './SharePointSitesPicker';
import type { SharePointSite } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import { ArrowUpIcon } from './icons';
import { SHORTCUTS, formatShortcut } from '../hooks/useKeyboardShortcuts';
import { useAuth } from '../context/AuthContext';

export type WebSearchMode = 'auto' | 'on' | 'off';

interface ChatInputProps {
  inputMessage: string;
  isLoading: boolean;
  uploadedFiles: UploadedFile[];
  hasEverHadFile?: boolean;
  onInputChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onSendMessage: (e: React.FormEvent) => void;
  onFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDeleteFile: (index: number) => void;
  webSearchMode?: WebSearchMode;
  isImageGenerationEnabled?: boolean;
  isDeepResearchEnabled?: boolean;
  onWebSearchModeChange?: (mode: WebSearchMode) => void;
  onImageGenerationToggle?: (enabled: boolean) => void;
  onDeepResearchToggle?: (enabled: boolean) => void;
  searchDomains?: string[];
  onSearchDomainsChange?: (domains: string[]) => void;
  isSharePointEnabled?: boolean;
  onSharePointToggle?: (enabled: boolean) => void;
  sharePointSites?: SharePointSite[];
  onSharePointSitesChange?: (sites: SharePointSite[]) => void;
  isChatStarted?: boolean;
  compact?: boolean;
  isAgentMode?: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({
  inputMessage,
  isLoading,
  uploadedFiles,
  hasEverHadFile = false,
  onInputChange,
  onSendMessage,
  onFileUpload,
  onDeleteFile,
  webSearchMode: webSearchModeProp = 'off',
  isImageGenerationEnabled: isImageGenerationEnabledProp = false,
  isDeepResearchEnabled: isDeepResearchEnabledProp = false,
  onWebSearchModeChange,
  onImageGenerationToggle,
  onDeepResearchToggle,
  searchDomains = [],
  onSearchDomainsChange,
  isSharePointEnabled = false,
  onSharePointToggle,
  sharePointSites = [],
  onSharePointSitesChange,
  isChatStarted = false,
  compact = false,
  isAgentMode = false
}) => {
  const [showToolsMenu, setShowToolsMenu] = useState(false);
  const [showSearchSubmenu, setShowSearchSubmenu] = useState(false);
  const webSearchMode = webSearchModeProp;
  const isImageGenerationEnabled = isImageGenerationEnabledProp;
  const isDeepResearchEnabled = isDeepResearchEnabledProp;
  const { colors, theme } = useTheme();
  const { user } = useAuth();

  const hasImageGenerationAccess =
    user?.roles?.includes("TheChatImageGeneration") ||
    user?.roles?.includes("Admin");

  const hasDeepResearchAccess =
    user?.roles?.includes("TheChatDeepResearch") ||
    user?.roles?.includes("Admin");

  const hasSharePointAccess =
    user?.roles?.includes("TheChatSharePoint") ||
    user?.roles?.includes("Admin");

  const hasFiles = uploadedFiles.length > 0;
  const hasUploadedImages = hasFiles && uploadedFiles[0].type.startsWith('image/');
  const hasUploadedNonImage = hasFiles && !uploadedFiles[0].type.startsWith('image/');

  // Auto-disable web search when file is uploaded
  useEffect(() => {
    if (hasFiles && webSearchMode !== 'off') {
      onWebSearchModeChange?.('off');
    }
  }, [hasFiles, webSearchMode, onWebSearchModeChange]);

  // Auto-disable image generation when non-image file is uploaded
  useEffect(() => {
    if (hasUploadedNonImage && isImageGenerationEnabled) {
      onImageGenerationToggle?.(false);
    }
  }, [hasUploadedNonImage, isImageGenerationEnabled, onImageGenerationToggle]);

  // Auto-disable deep research when image is uploaded
  useEffect(() => {
    if (hasUploadedImages && isDeepResearchEnabled) {
      onDeepResearchToggle?.(false);
    }
  }, [hasUploadedImages, isDeepResearchEnabled, onDeepResearchToggle]);

  const toolsMenuRef = useRef<HTMLDivElement>(null);
  const toolsButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (toolsMenuRef.current && !toolsMenuRef.current.contains(event.target as Node) &&
          toolsButtonRef.current && !toolsButtonRef.current.contains(event.target as Node)) {
        setShowToolsMenu(false);
        setShowSearchSubmenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleToolsClick = () => {
    setShowToolsMenu(!showToolsMenu);
    if (showToolsMenu) setShowSearchSubmenu(false);
  };

  // Mode display config
  const modeConfig: Record<WebSearchMode, { label: string; color: string; bgColor: string; hoverBg: string; borderColor: string }> = {
    auto: { label: 'Search: Auto', color: '#7e22ce', bgColor: '#f3e8ff', hoverBg: '#e9d5ff', borderColor: '#a855f7' },
    on:   { label: 'Search: On', color: '#1d4ed8', bgColor: '#dbeafe', hoverBg: '#bfdbfe', borderColor: '#3b82f6' },
    off:  { label: '', color: '', bgColor: '', hoverBg: '', borderColor: '' },
  };

  const isSearchActive = webSearchMode !== 'off';
  const searchDisabled = hasFiles || isDeepResearchEnabled || isImageGenerationEnabled || isSharePointEnabled;
  // "Auto" lets the model choose its own search arguments, so a fixed site list only applies to these two.
  const showSourcesPicker = !!onSearchDomainsChange && !isAgentMode && (webSearchMode === 'on' || isDeepResearchEnabled);

  return (
    <div style={{
      position: 'relative',
      ...(compact ? { maxWidth: '800px', margin: '0 auto' } : {})
    }}>
      {!isAgentMode && (
        <FileUpload
          uploadedFiles={uploadedFiles}
          onFileUpload={onFileUpload}
          onDeleteFile={onDeleteFile}
        />
      )}

      {/* Input bubble container */}
      <div
        data-shortcut={formatShortcut(SHORTCUTS.focusInput)}
        data-shortcut-pos="inside-top"
        style={{
        border: `1px solid ${colors.border}`,
        borderRadius: '20px',
        backgroundColor: colors.surface,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        padding: '20px 24px 16px 24px'
      }}>
        <form onSubmit={onSendMessage}>
          {/* Text input area */}
          <textarea
            data-chat-input
            value={inputMessage}
            onChange={onInputChange}
            placeholder="Type your message here..."
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onSendMessage(e);
              }
            }}
            style={{
              width: '100%',
              border: 'none',
              fontSize: '16px',
              outline: 'none',
              minHeight: '60px',
              maxHeight: '200px',
              resize: 'none',
              boxSizing: 'border-box',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              lineHeight: '1.5',
              backgroundColor: 'transparent',
              color: colors.textPrimary,
              marginBottom: '5px'
            }}
            disabled={isLoading}
            autoFocus
          />

          {/* Button row */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: '40px'
          }}>
            {/* Left side buttons */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginLeft: isAgentMode ? '0' : '45px',
              height: '40px'
            }}>
              {/* Tools button - hidden in agent mode */}
              {!isAgentMode && (
                <button
                  ref={toolsButtonRef}
                  type="button"
                  onClick={handleToolsClick}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 16px',
                    borderRadius: '20px',
                    border: `1px solid ${colors.border}`,
                    backgroundColor: colors.surface,
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: colors.textSecondary,
                    transition: 'all 0.2s ease',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    height: '40px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = colors.buttonHover;
                    e.currentTarget.style.borderColor = colors.border;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = colors.surface;
                    e.currentTarget.style.borderColor = colors.border;
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="4" y1="21" x2="4" y2="14"/>
                    <line x1="4" y1="10" x2="4" y2="3"/>
                    <line x1="12" y1="21" x2="12" y2="12"/>
                    <line x1="12" y1="8" x2="12" y2="3"/>
                    <line x1="20" y1="21" x2="20" y2="16"/>
                    <line x1="20" y1="12" x2="20" y2="3"/>
                    <line x1="1" y1="14" x2="7" y2="14"/>
                    <line x1="9" y1="8" x2="15" y2="8"/>
                    <line x1="17" y1="16" x2="23" y2="16"/>
                  </svg>
                  Tools
                </button>
              )}

              {/* Web Search Mode Indicator */}
              {isSearchActive && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  borderRadius: '16px',
                  backgroundColor: hasFiles ? colors.surfaceElevated : modeConfig[webSearchMode].bgColor,
                  border: `1px solid ${hasFiles ? colors.border : modeConfig[webSearchMode].borderColor}`,
                  fontSize: '14px',
                  color: hasFiles ? colors.textMuted : modeConfig[webSearchMode].color,
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  cursor: hasFiles ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  opacity: hasFiles ? 0.6 : 1
                }}
                onClick={() => {
                  if (!hasFiles) {
                    onWebSearchModeChange?.('off');
                  }
                }}
                onMouseEnter={(e) => {
                  if (!hasFiles) {
                    e.currentTarget.style.backgroundColor = modeConfig[webSearchMode].hoverBg;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!hasFiles) {
                    e.currentTarget.style.backgroundColor = modeConfig[webSearchMode].bgColor;
                  }
                }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/>
                    <path d="M2 12h20"/>
                  </svg>
                  {modeConfig[webSearchMode].label}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </div>
              )}

              {/* Image Generation Indicator */}
              {isImageGenerationEnabled && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  borderRadius: '16px',
                  backgroundColor: '#fef3c7',
                  border: '1px solid #f59e0b',
                  fontSize: '14px',
                  color: '#b45309',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onClick={() => {
                  onImageGenerationToggle?.(false);
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#fde68a';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#fef3c7';
                }}
                title="Image Generation"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5"/>
                    <polyline points="21 15 16 10 5 21"/>
                  </svg>
                  Image Generation
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </div>
              )}

              {/* Deep Research Indicator */}
              {isDeepResearchEnabled && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  borderRadius: '16px',
                  backgroundColor: '#f3e8ff',
                  border: '1px solid #a855f7',
                  fontSize: '14px',
                  color: '#7e22ce',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onClick={() => {
                  onDeepResearchToggle?.(false);
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#e9d5ff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#f3e8ff';
                }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                  </svg>
                  Deep Research
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </div>
              )}

              {showSourcesPicker && (
                <SearchSourcesPicker domains={searchDomains} onChange={onSearchDomainsChange!} />
              )}

              {/* SharePoint Indicator */}
              {isSharePointEnabled && !isAgentMode && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  borderRadius: '16px',
                  backgroundColor: '#e0f2fe',
                  border: '1px solid #0ea5e9',
                  fontSize: '14px',
                  color: '#0369a1',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onClick={() => {
                  onSharePointToggle?.(false);
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#bae6fd';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#e0f2fe';
                }}
                title="Turn off SharePoint"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="8" y1="13" x2="16" y2="13"/>
                    <line x1="8" y1="17" x2="13" y2="17"/>
                  </svg>
                  SharePoint
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </div>
              )}

              {isSharePointEnabled && !isAgentMode && onSharePointSitesChange && (
                <SharePointSitesPicker sites={sharePointSites} onChange={onSharePointSitesChange} />
              )}
            </div>

            {/* Right side button */}
            <button
              type="submit"
              disabled={isLoading || !inputMessage.trim()}
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                border: 'none',
                backgroundColor: isLoading || !inputMessage.trim() ? colors.border : colors.sendButton,
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: isLoading || !inputMessage.trim() ? 'not-allowed' : 'pointer',
                fontSize: '18px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!isLoading && inputMessage.trim()) {
                  e.currentTarget.style.backgroundColor = colors.sendButtonHover;
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading && inputMessage.trim()) {
                  e.currentTarget.style.backgroundColor = colors.sendButton;
                }
              }}
            >
              {isLoading ? (
                <div style={{
                  display: 'flex',
                  gap: '2px',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {[0, 1, 2].map((index) => (
                    <div
                      key={index}
                      style={{
                        width: '4px',
                        height: '4px',
                        borderRadius: '50%',
                        backgroundColor: 'currentColor',
                        animation: `cv-dot-pulse 1.4s ease-in-out ${index * 0.2}s infinite`,
                        opacity: 0.4
                      }}
                    />
                  ))}
                </div>
              ) : <ArrowUpIcon size={18} strokeWidth={2.5} />}
            </button>
          </div>
        </form>
      </div>

      {/* Tools dropdown menu */}
      {showToolsMenu && (
        <div
          ref={toolsMenuRef}
          style={{
            position: 'absolute',
            bottom: '60px',
            left: '0px',
            backgroundColor: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: '12px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
            padding: '8px',
            minWidth: '220px',
            zIndex: 1000
          }}
        >
          {/* Web Search — parent item with expandable submenu */}
          <div style={{ position: 'relative' }}>
            <button
              type="button"
              disabled={searchDisabled}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: 'none',
                backgroundColor: showSearchSubmenu ? colors.surfaceElevated : 'transparent',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                borderRadius: '8px',
                cursor: searchDisabled ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                color: searchDisabled ? colors.textMuted : colors.textSecondary,
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                transition: 'background-color 0.2s ease',
                opacity: searchDisabled ? 0.6 : 1
              }}
              onMouseEnter={(e) => {
                if (!searchDisabled) {
                  e.currentTarget.style.backgroundColor = colors.surfaceElevated;
                  setShowSearchSubmenu(true);
                }
              }}
              onClick={() => {
                if (!searchDisabled) {
                  setShowSearchSubmenu(!showSearchSubmenu);
                }
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/>
                <path d="M2 12h20"/>
              </svg>
              <span style={{ flex: 1, textAlign: 'left' }}>Search the web</span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{
                transition: 'transform 0.2s ease',
                transform: showSearchSubmenu ? 'rotate(90deg)' : 'rotate(0deg)'
              }}>
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>

            {/* Search mode submenu — inline expansion */}
            {showSearchSubmenu && !searchDisabled && (
              <div style={{
                overflow: 'hidden',
                transition: 'max-height 0.2s ease',
                padding: '4px 0 4px 16px',
              }}>
                {([
                  {
                    mode: 'auto' as WebSearchMode,
                    label: 'Auto',
                    description: 'AI decides when to search',
                    icon: (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2Z"/>
                        <path d="M12 6v6l4 2"/>
                      </svg>
                    ),
                    color: '#7e22ce',
                    bgActive: '#f3e8ff',
                  },
                  {
                    mode: 'on' as WebSearchMode,
                    label: 'Always',
                    description: 'Search on every message',
                    icon: (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8"/>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                      </svg>
                    ),
                    color: '#1d4ed8',
                    bgActive: '#dbeafe',
                  },
                  {
                    mode: 'off' as WebSearchMode,
                    label: 'Off',
                    description: 'No web search access',
                    icon: (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
                      </svg>
                    ),
                    color: colors.textMuted,
                    bgActive: colors.surfaceElevated,
                  },
                ]).map((opt) => {
                  const isSelected = webSearchMode === opt.mode;
                  return (
                    <button
                      key={opt.mode}
                      type="button"
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        border: 'none',
                        backgroundColor: isSelected ? opt.bgActive : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        color: isSelected ? opt.color : colors.textSecondary,
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                        transition: 'all 0.15s ease',
                        position: 'relative',
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) e.currentTarget.style.backgroundColor = colors.surfaceElevated;
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                      onClick={() => {
                        onWebSearchModeChange?.(opt.mode);
                        setShowSearchSubmenu(false);
                        setShowToolsMenu(false);
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', color: isSelected ? opt.color : colors.textMuted }}>
                        {opt.icon}
                      </span>
                      <div style={{ flex: 1, textAlign: 'left' }}>
                        <div style={{ fontWeight: isSelected ? 600 : 400 }}>{opt.label}</div>
                        <div style={{ fontSize: '11px', color: colors.textMuted, marginTop: '1px' }}>{opt.description}</div>
                      </div>
                      {isSelected && (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={opt.color} strokeWidth="2.5">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Image Generation button */}
          <button
            type="button"
            disabled={!!(!hasImageGenerationAccess || hasUploadedNonImage || isSearchActive || isDeepResearchEnabled || isSharePointEnabled)}
            title={!hasImageGenerationAccess ? 'You do not have access to this feature' : undefined}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: 'none',
              backgroundColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              borderRadius: '8px',
              cursor: (!hasImageGenerationAccess || hasUploadedNonImage || isSearchActive || isDeepResearchEnabled || isSharePointEnabled) ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              color: (!hasImageGenerationAccess || hasUploadedNonImage || isSearchActive || isDeepResearchEnabled || isSharePointEnabled) ? colors.textMuted : colors.textSecondary,
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              transition: 'background-color 0.2s ease',
              opacity: (!hasImageGenerationAccess || hasUploadedNonImage || isSearchActive || isDeepResearchEnabled || isSharePointEnabled) ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (hasImageGenerationAccess && !hasUploadedNonImage && !isSearchActive && !isDeepResearchEnabled && !isSharePointEnabled) {
                e.currentTarget.style.backgroundColor = colors.surfaceElevated;
              }
            }}
            onMouseLeave={(e) => {
              if (hasImageGenerationAccess && !hasUploadedNonImage && !isSearchActive && !isDeepResearchEnabled && !isSharePointEnabled) {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
            onClick={() => {
              if (hasImageGenerationAccess && !hasUploadedNonImage && !isSearchActive && !isDeepResearchEnabled && !isSharePointEnabled) {
                const newState = !isImageGenerationEnabled;
                if (newState) {
                  onWebSearchModeChange?.('off');
                }
                setShowToolsMenu(false);
                onImageGenerationToggle?.(newState);
              }
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <polyline points="21 15 16 10 5 21"/>
            </svg>
            Generate image
          </button>

          {/* SharePoint button */}
          {onSharePointToggle && (
            <button
              type="button"
              disabled={!hasSharePointAccess}
              title={!hasSharePointAccess ? 'You do not have access to this feature' : undefined}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: 'none',
                backgroundColor: 'transparent',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                borderRadius: '8px',
                cursor: !hasSharePointAccess ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                color: !hasSharePointAccess ? colors.textMuted : colors.textSecondary,
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                transition: 'background-color 0.2s ease',
                opacity: !hasSharePointAccess ? 0.6 : 1
              }}
              onMouseEnter={(e) => {
                if (hasSharePointAccess) {
                  e.currentTarget.style.backgroundColor = colors.surfaceElevated;
                }
              }}
              onMouseLeave={(e) => {
                if (hasSharePointAccess) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
              onClick={() => {
                if (hasSharePointAccess) {
                  const newState = !isSharePointEnabled;
                  if (newState) {
                    onWebSearchModeChange?.('off');
                    onImageGenerationToggle?.(false);
                    onDeepResearchToggle?.(false);
                  }
                  setShowToolsMenu(false);
                  setShowSearchSubmenu(false);
                  onSharePointToggle?.(newState);
                }
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="8" y1="13" x2="16" y2="13"/>
                <line x1="8" y1="17" x2="13" y2="17"/>
              </svg>
              {isSharePointEnabled ? 'Turn off SharePoint' : 'Search SharePoint'}
            </button>
          )}

          {/* Deep Research button - only show when chat hasn't started */}
          {!isChatStarted && (
            <button
              type="button"
              disabled={!!(!hasDeepResearchAccess || hasUploadedImages || isSharePointEnabled)}
              title={!hasDeepResearchAccess ? 'You do not have access to this feature' : undefined}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: 'none',
                backgroundColor: 'transparent',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                borderRadius: '8px',
                cursor: (!hasDeepResearchAccess || hasUploadedImages || isSharePointEnabled) ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                color: (!hasDeepResearchAccess || hasUploadedImages || isSharePointEnabled) ? colors.textMuted : colors.textSecondary,
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                transition: 'background-color 0.2s ease',
                opacity: (!hasDeepResearchAccess || hasUploadedImages || isSharePointEnabled) ? 0.6 : 1
              }}
              onMouseEnter={(e) => {
                if (hasDeepResearchAccess && !hasUploadedImages && !isSharePointEnabled) {
                  e.currentTarget.style.backgroundColor = colors.surfaceElevated;
                }
              }}
              onMouseLeave={(e) => {
                if (hasDeepResearchAccess && !hasUploadedImages && !isSharePointEnabled) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
              onClick={() => {
                if (hasDeepResearchAccess && !hasUploadedImages && !isSharePointEnabled) {
                  const newState = !isDeepResearchEnabled;
                  if (newState) {
                    onWebSearchModeChange?.('off');
                    onImageGenerationToggle?.(false);
                  }
                  setShowToolsMenu(false);
                  onDeepResearchToggle?.(newState);
                }
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
              </svg>
              Deep research
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatInput;
